import * as THREE from "three";
import { EventBus } from "../EventBus.js";
import type { SpriteManager } from "../sprites/SpriteManager.js";

/**
 * S24 — visual effects for spell casts.
 *
 * Listens for `combat:spell_cast` and animates a hand-authored visual
 * keyed off `spellId`. Three named spells are supported (magic_missile,
 * cure_wounds, fireball); everything else gets a generic flash so
 * casters always have *some* feedback.
 *
 * Effects own their own meshes/lights and clean up on completion via
 * the `active` list; SpellVfx.tick(delta) is called from the main loop.
 */

const CELL_SIZE = 1;
const SPRITE_Y = 0.5;
const CASTER_Y = 0.5;

interface ActiveEffect {
  elapsed: number;
  duration: number;
  update(t: number, elapsed: number): void;
  dispose(): void;
}

export class SpellVfx {
  private readonly active: ActiveEffect[] = [];
  private readonly off: () => void;

  constructor(
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.Camera,
    private readonly getSpriteManager: () => SpriteManager | null,
  ) {
    this.off = EventBus.on("combat:spell_cast", (e) => {
      this.dispatch(e.spellId, e.casterId, e.targets, e.cellTarget);
    });
  }

  dispose(): void {
    this.off();
    for (const fx of this.active) fx.dispose();
    this.active.length = 0;
  }

  tick(delta: number): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const fx = this.active[i]!;
      fx.elapsed += delta;
      const t = Math.min(1, fx.elapsed / fx.duration);
      fx.update(t, fx.elapsed);
      if (fx.elapsed >= fx.duration) {
        fx.dispose();
        this.active.splice(i, 1);
      }
    }
  }

  private dispatch(
    spellId: string,
    casterId: string,
    targets: readonly string[],
    cellTarget: { x: number; z: number } | null,
  ): void {
    const casterPos = this.resolveCasterPos(casterId);
    switch (spellId) {
      case "magic_missile":
        for (let i = 0; i < targets.length; i++) {
          const tp = this.resolveTargetPos(targets[i]!);
          if (tp) this.addMissile(casterPos, tp, i * 100);
        }
        return;
      case "cure_wounds":
        for (const id of targets) {
          const tp = this.resolveTargetPos(id);
          if (tp) this.addCureColumn(tp);
        }
        return;
      case "fireball": {
        const center = cellTarget
          ? new THREE.Vector3(cellTarget.x * CELL_SIZE, 0.4, cellTarget.z * CELL_SIZE)
          : (this.resolveTargetPos(targets[0] ?? "") ?? casterPos);
        this.addFireball(center);
        return;
      }
      default:
        for (const id of targets) {
          const tp = this.resolveTargetPos(id);
          if (tp) this.addGenericFlash(tp);
        }
    }
  }

  private resolveCasterPos(casterId: string): THREE.Vector3 {
    const sm = this.getSpriteManager();
    const sp = sm?.getSpritePos(casterId);
    if (sp) return sp.clone().setY(CASTER_Y);
    // Party caster — use camera position.
    return this.camera.position.clone().setY(CASTER_Y);
  }

  private resolveTargetPos(id: string): THREE.Vector3 | null {
    const sm = this.getSpriteManager();
    const sp = sm?.getSpritePos(id);
    if (sp) return sp.clone().setY(SPRITE_Y);
    return null;
  }

  // ── Magic Missile: bright blue core + outer glow + point light ──────────
  // Damage application in CombatManager is timed to this schedule:
  // dart i fires at i*100ms, flightDuration = 500ms. Keep them in sync.
  private addMissile(from: THREE.Vector3, to: THREE.Vector3, delayMs: number): void {
    const coreGeo = new THREE.SphereGeometry(0.14, 16, 12);
    const coreMat = new THREE.MeshBasicMaterial({ color: 0xe8f4ff, transparent: true });
    const core = new THREE.Mesh(coreGeo, coreMat);

    const glowGeo = new THREE.SphereGeometry(0.32, 16, 12);
    const glowMat = new THREE.MeshBasicMaterial({
      color: 0x6fb6ff,
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const glow = new THREE.Mesh(glowGeo, glowMat);

    const light = new THREE.PointLight(0x6fb6ff, 3.5, 6);
    core.position.copy(from);
    glow.position.copy(from);
    light.position.copy(from);
    core.visible = false;
    glow.visible = false;
    light.visible = false;
    this.scene.add(core, glow, light);

    const start = from.clone();
    const end = to.clone();
    const delay = delayMs / 1000;
    const flightDuration = 0.5;

    this.active.push({
      elapsed: 0,
      duration: delay + flightDuration,
      update: (_t, elapsed) => {
        const localT = Math.max(0, (elapsed - delay) / flightDuration);
        if (localT <= 0) return;
        core.visible = true;
        glow.visible = true;
        light.visible = true;
        const p = start.clone().lerp(end, localT);
        p.y += Math.sin(localT * Math.PI) * 0.4;
        core.position.copy(p);
        glow.position.copy(p);
        light.position.copy(p);
        const fade = 1 - Math.max(0, (localT - 0.75) / 0.25);
        coreMat.opacity = fade;
        glowMat.opacity = 0.45 * fade;
        const pulse = 1 + Math.sin(elapsed * 30) * 0.15;
        glow.scale.setScalar(pulse);
        light.intensity = 3.5 * fade;
      },
      dispose: () => {
        this.scene.remove(core, glow, light);
        coreGeo.dispose();
        coreMat.dispose();
        glowGeo.dispose();
        glowMat.dispose();
      },
    });
  }

  // ── Cure Wounds: golden sparkle column rising from target ───────────────
  private addCureColumn(at: THREE.Vector3): void {
    const group = new THREE.Group();
    const sparkles: Array<{ mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial; offset: number; phase: number }> = [];
    const geo = new THREE.SphereGeometry(0.05, 6, 4);
    for (let i = 0; i < 8; i++) {
      const mat = new THREE.MeshBasicMaterial({ color: 0xffd870, transparent: true });
      const mesh = new THREE.Mesh(geo, mat);
      const angle = (i / 8) * Math.PI * 2;
      const r = 0.2;
      mesh.position.set(Math.cos(angle) * r, 0, Math.sin(angle) * r);
      group.add(mesh);
      sparkles.push({ mesh, mat, offset: angle, phase: Math.random() * 0.3 });
    }
    group.position.copy(at).setY(0);
    this.scene.add(group);

    this.active.push({
      elapsed: 0,
      duration: 0.7,
      update: (t) => {
        for (const s of sparkles) {
          const pt = Math.min(1, t + s.phase);
          s.mesh.position.y = pt * 1.6;
          const fade = pt < 0.7 ? 1 : 1 - (pt - 0.7) / 0.3;
          s.mat.opacity = fade;
        }
      },
      dispose: () => {
        this.scene.remove(group);
        geo.dispose();
        for (const s of sparkles) s.mat.dispose();
      },
    });
  }

  // ── Fireball: expanding orange sphere at AoE cell, fades out ────────────
  private addFireball(at: THREE.Vector3): void {
    const geo = new THREE.SphereGeometry(1, 16, 12);
    const mat = new THREE.MeshBasicMaterial({
      color: 0xff7028,
      transparent: true,
      opacity: 0.9,
    });
    const sphere = new THREE.Mesh(geo, mat);
    sphere.position.copy(at);
    const light = new THREE.PointLight(0xff8030, 3, 6);
    light.position.copy(at);
    this.scene.add(sphere, light);

    this.active.push({
      elapsed: 0,
      duration: 0.7,
      update: (t) => {
        const radius = 0.3 + t * 1.8;
        sphere.scale.setScalar(radius);
        mat.opacity = 0.9 * (1 - t);
        light.intensity = 3 * (1 - t);
      },
      dispose: () => {
        this.scene.remove(sphere, light);
        geo.dispose();
        mat.dispose();
      },
    });
  }

  // ── Generic flash on target — soft white expanding ring ─────────────────
  private addGenericFlash(at: THREE.Vector3): void {
    const geo = new THREE.SphereGeometry(0.4, 12, 8);
    const mat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.6,
    });
    const sphere = new THREE.Mesh(geo, mat);
    sphere.position.copy(at);
    this.scene.add(sphere);
    this.active.push({
      elapsed: 0,
      duration: 0.35,
      update: (t) => {
        sphere.scale.setScalar(1 + t * 1.2);
        mat.opacity = 0.6 * (1 - t);
      },
      dispose: () => {
        this.scene.remove(sphere);
        geo.dispose();
        mat.dispose();
      },
    });
  }
}
